import { memo, useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { CSSTransition } from "react-transition-group";
import { ReactComponent as Control } from "static/icons/double-right.svg";
import { resizeViewer, isolateElements, closeModel } from "utils/builder/IScheduleLogic";
import "./DashboardProj.scss";
import EditorSection from "components/EditorSection";
import SidebarModule from "components/sidebarComponents/SidebarModule";
import UniversalLoader from "components/uiComponents/UniversalLoader";
import { useDashboardState } from "components/DashboardContext";
import { ModalContext } from "components/ModalContext";
import ProofModal from "components/modals/ProofModal";
import { ReactComponent as CloseIcon } from "static/icons/close-small.svg";
import { getFoldersCollections, selectCollectionOnView, unselectCollectionOnView } from "store/actions/collections";
import { ElementsAction, closeTab, setTabActive } from "store/actions/table";
import { tabType } from "utils/data";
import { resetColorSideBarAC } from "store/actions/colorSidebar";
import { SidebarAdvancedFilters } from "components/sidebarComponents/SidebarAdvancedFilters";
import { DashboardInfoTableV2 } from "components/DashboardInfoTableV2";
import useSetting from "components/sidebarComponents/SidebarProject/use-settings";
import { setCrossTabNamespace } from "utils/crossTabSync";
import classNames from "classnames";

const DashboardProj = ({ location }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const tabs = useRef();
  const tabsWrapper = useRef();
  const scrollStep = 200;
  const layoutRef = useRef();
  const topPaneRef = useRef();

  const { handleModal } = useContext(ModalContext);
  const { isTableHidden, setIsTableHidden } = useDashboardState();

  const [sidebarIsOpened, setSidebarIsOpened] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [activeLeftControl, setActiveLeftControl] = useState(false);
  const [activeRightControl, setActiveRightControl] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasTable, setHasTable] = useState(false);
  const [hideTable, setHideTable] = useState(false);
  const [timelineHide, setTimelineHide] = useState(false);
  const timelinePrevHideRef = useRef(null);

  const { closeAllSettings, resetProjectStates } = useSetting();

  const modelId = location?.state?.project?.idToOpen || null;
  
  const {
    viewer: { quickCounterData, templates },
  } = useSelector((state) => state);
  
  const rawTable = useSelector((state) => state.table);
  const table = useMemo(() => {
    if (!modelId) return rawTable;
    
    const { getCurrentNamespace } = require('utils/crossTabSync');
    const currentNs = getCurrentNamespace();
    if (!currentNs || currentNs !== modelId) {
      return {
        ...rawTable,
        tabs: [],
        currentTableState: { ...rawTable.currentTableState, selectedRows: [] }
      };
    }
    
    const filteredTabs = rawTable.tabs.filter((tab) => {
      if (tab.modelId) {
        return tab.modelId === modelId;
      }
      return currentNs === modelId;
    });
    
    return {
      ...rawTable,
      tabs: filteredTabs
    };
  }, [rawTable, modelId]);

  if (modelId) {
    setCrossTabNamespace(modelId);
  }

  useEffect(() => {
    if (modelId) setCrossTabNamespace(modelId);
  }, [modelId]);

  useEffect(() => {
    return () => {
      resetProjectStates();
      closeModel();
    };
  }, []);

  useEffect(() => {
    const tabsEl = tabs.current;
    const tabsWrapperEl = tabsWrapper.current;
    if (
      tabsEl?.scrollWidth > tabsWrapperEl?.scrollWidth &&
      tabsEl?.scrollWidth - tabsEl?.scrollLeft !== tabsEl?.clientWidth
    ) {
      setActiveRightControl(true);
    } else {
      setActiveRightControl(false);
    }
    setActiveLeftControl((tabsEl?.scrollLeft ?? 0) > 0);
  }, [table?.tabs?.length]);

  useEffect(() => {
    dispatch(getFoldersCollections());
    table?.tabs.forEach((tab) => {
      if (tab?.active) setActiveTab(tab);
    });
    const tableExists = table.tabs.length > 0;

    setHasTable(tableExists);
    if (!tableExists) {
      setIsTableHidden(true);
      dispatch(ElementsAction(null, modelId));
    }
  }, [table, table?.active, dispatch, setIsTableHidden]);

  const handleChangeSidebar = (status) => {
    setSidebarIsOpened(status);
    setTimeout(resizeViewer, 310);
  };

  const handleActiveTab = (tab) => {
    if (!tab.active) {
      closeAllSettings();
      dispatch(resetColorSideBarAC());
      dispatch(setTabActive(tab));
      unselectCollectionOnView();
      if (tab.type === tabType.saved) {
        selectCollectionOnView([tab.collection]);
      } else {
        isolateElements(tab?.lineElements?.flat());
      }
    }
  };

  const handleCloseTab = (event, tab) => {
    if (tab.type === tabType.unsaved) {
      handleModal(
        <ProofModal
          onCloseModal={handleCancel}
          proofCallback={() => closeDownTab(event, tab)}
          cancelButtonText={t('back_to_editing')}
          proofButtonText={t('close_tab')}
        >
          <div>
            {t('you_want_to_close')} <b>“{tab.name} - {t('unsaved')}”</b> {t('without_saving')}. {t('all_your_changes_will_discarded')}
          </div>
        </ProofModal>
      );
    } else {
      closeDownTab(event, tab);
    }
  };

  const closeDownTab = (event, tab) => {
    event.stopPropagation();
    dispatch(closeTab(tab));
    unselectCollectionOnView();
    if (tab.type === tabType.unsaved) handleCancel();
  };

  const handleCancel = () => handleModal(null, true);

  const nextTab = () => {
    const tabsEl = tabs.current;
    tabsEl.scroll(tabsEl.scrollLeft + scrollStep, 0);
    setActiveRightControl(tabsEl.scrollWidth - tabsEl.scrollLeft !== tabsEl.clientWidth);
    setActiveLeftControl(tabsEl.scrollLeft !== 0);
  };

  const prevTab = () => {
    const tabsEl = tabs.current;
    tabsEl.scroll(tabsEl.scrollLeft - scrollStep, 0);
    setActiveLeftControl(tabsEl.scrollLeft !== 0);
    setActiveRightControl(tabsEl.scrollWidth - tabsEl.scrollLeft !== tabsEl.clientWidth);
  };

  useEffect(() => {
    window.__bs_hideCurrentTable = () => {
      setHideTable(true);
      setIsTableHidden(true);
      if (topPaneRef.current) {
        topPaneRef.current.style.height = "100%";
      }
      setTimeout(resizeViewer, 50);
    };
    return () => {
      try { delete window.__bs_hideCurrentTable; } catch {}
    };
  }, [setIsTableHidden]);

  const showSignalKey = useMemo(
    () => (modelId ? `host:show-table:${modelId}` : null),
    [modelId]
  );

  const isFullHeigthModel = useMemo(() => {
    return (isTableHidden || !hasTable || timelineHide || hideTable);
  }, [hideTable, timelineHide, hasTable, isTableHidden]);

  const showTableNow = useCallback(() => {
    setHideTable(false);
    setIsTableHidden(!hasTable ? true : false);
    if (topPaneRef.current) {
      topPaneRef.current.style.height = "";
    }
    setTimeout(resizeViewer, 50);
  }, [hasTable, setIsTableHidden]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.storageArea !== localStorage) return;

      if (showSignalKey && e.key === showSignalKey) {
        showTableNow();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [showSignalKey, showTableNow]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !hasTable || hideTable) return;
      const layoutTop = layoutRef.current.getBoundingClientRect().top;
      const newHeight = e.clientY - layoutTop;
      topPaneRef.current.style.height = `${newHeight}px`;
    };
    const stopDragging = () => setIsDragging(false);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", stopDragging);
    resizeViewer();
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopDragging);
    };
  }, [isDragging, hasTable, hideTable]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.storageArea !== localStorage || !e.newValue) return;
      if (!modelId) return;
      if (!e.key || !e.key.startsWith(`table-ctrl:${modelId}:`)) return;
      try {
        const msg = JSON.parse(e.newValue);
        const elementIds = msg?.payload?.elementIds || [];
        dispatch(ElementsAction(elementIds.length ? elementIds : null, modelId));
      } catch {}
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch, modelId]);

  if (location == null || location.state == null) {
    document.location.href = "/";
    return null;
  }

  return (
    <div className="dashboard-proj">
      <Toaster position="top-center" toastOptions={{ style: { maxWidth: '480px', margin: 0, padding: 0, borderRadius: '20px' } }} />
      <SidebarModule isOpened={sidebarIsOpened} onChangeSidebar={handleChangeSidebar} />
      <SidebarAdvancedFilters sideBarModuleOpened={sidebarIsOpened}/>
      <div className="main-resizable-layout" ref={layoutRef}>
        <div
          className={classNames({
            "resizable-top-pane": true,
            "resizable-top-pane--full": isFullHeigthModel,
          })}
          style={{height: '350px'}}
          ref={topPaneRef}
        >
          <EditorSection
            autodesk={window.Autodesk}
            projectData={location.state.project}
            quickCounterData={quickCounterData}
            table={table}
            sidebarIsOpened={sidebarIsOpened}
            onTimelineToggle={(open) => {
              if (!hasTable || hideTable) return;
              if (open) {
                timelinePrevHideRef.current = hideTable;
                setTimelineHide(true);
                resizeViewer();
              } else {
                setTimelineHide(false);
                if (timelinePrevHideRef.current !== null) {
                  setHideTable(timelinePrevHideRef.current);
                  timelinePrevHideRef.current = null;
                }
                setTimeout(() => resizeViewer(), 120);
              }
            }}
          />
        </div>

        {hasTable && !hideTable && !timelineHide && (
          <div
            className="resizer-bar"
            onMouseDown={() => !timelineHide && setIsDragging(true)}
            style={timelineHide ? { display: 'none' } : undefined}
          ></div>
        )}

        {hasTable && !hideTable && !timelineHide && (
          <div
            className="resizable-bottom-pane"
            style={
              timelineHide
                ? { flex: '0 0 0', height: 0, minHeight: 0, overflow: 'hidden' }
                : undefined
            }
          >
            <div className="tabs-wrapper" ref={tabsWrapper}>
              {activeLeftControl && (
                <div className="tabs-control left-control" onClick={prevTab}>
                  <Control />
                </div>
              )}
              <div className="tabs" ref={tabs}>
                {table.tabs.map((tab) => (
                  <div
                    key={tab.id}
                    className={`tab ${activeTab?.id === tab.id && "tab-active"}`}
                    onClick={() => handleActiveTab(tab)}
                  >
                    {tab.type === tabType.saved && (
                      <span className="tab__color" style={{ backgroundColor: tab.color }}></span>
                    )}
                    <span className="tab__text">{tab.name}</span>
                    {tab.type === tabType.unsaved && (
                      <span className="drop-down-add-to__unsaved">&nbsp;- {t('unsaved')}</span>
                    )}
                    <CloseIcon className="tab__close-icon" onClick={(event) => handleCloseTab(event, tab)} />
                  </div>
                ))}
              </div>
              {activeRightControl && (
                <div className="tabs-control right-control" onClick={nextTab}>
                  <Control />
                </div>
              )}
            </div>

            {activeTab && (
              <DashboardInfoTableV2
                table={activeTab}
                projectData={location.state.project}
                handleChangeSidebar={handleChangeSidebar}
              />
            )}
          </div>
        )}
        <CSSTransition in={!templates} timeout={150} unmountOnExit classNames="animation">
          <div className="dashboard-proj__preloader">
            <UniversalLoader
              label={`${t('loading')}...`}
              labelColor="gray5"
              preloaderColor="black"
              preloaderBgColor="gray5"
              position="center"
              isWithoutContainerBg
            />
          </div>
        </CSSTransition>
      </div>
    </div>
  );
};

export default memo(DashboardProj);